import * as yup from 'yup'

import {
  configurationsFields as CF,
  configRootFields,
  ERP_FIELDS,
} from 'components/configuration/utils'
import { EMAIL_REGEX, HOST_REGEX, MULTIPLE_EMAIL_REGEX } from 'utils/constants'

const NUMBER_REGEX = /^([1-9][0-9]*)$/

const arrayOfStringSchema = yup
  .array()
  .of(yup.string())
  .min(1, 'Must contain at least one element')
  .optional()

const erpBearerTokenSchema = yup.object().shape({
  [ERP_FIELDS.URL]: yup
    .string()
    .trim()
    .required('End Point is a required field'),
  [ERP_FIELDS.BEARER_TOKEN]: yup
    .string()
    .trim()
    .required('Bearer Token is a required field'),
})

const erpSchema = yup.object().shape({
  [ERP_FIELDS.URL]: yup
    .string()
    .trim()
    .required('End Point is a required field'),
  [ERP_FIELDS.USERNAME]: yup
    .string()
    .trim()
    .required('Username is a required field'),
  [ERP_FIELDS.PASSWORD]: yup
    .string()
    .trim()
    .required('Password is a required field'),
})

export const configurationSchema = yup
  .object()
  .shape({
    // ERP - MASTER
    [configRootFields.ERP_MASTER]: erpSchema,
    // ERP - INVENTORY
    [configRootFields.ERP_INVENTORY]: erpSchema,
    // ERP - PRICING
    [configRootFields.ERP_PRICING]: erpSchema,
    // ERP - ORDER Create
    [configRootFields.ERP_ORDER_CREATE]: erpSchema,
    // ERP - Reconciliation
    [configRootFields.ERP_RECONCILIATION]: erpSchema,
    // ERP - Reconciliation
    [configRootFields.LAST_MILE]: erpBearerTokenSchema,
    // ERP - Block Sale on POS
    [configRootFields.BLOCK_SALE_POS]: erpSchema,
    // Fresh System
    [configRootFields.FRESH]: erpSchema,

    // Fresh System
    [configRootFields.RMA_REASON]: arrayOfStringSchema,
    [configRootFields.RMA_CONDITIONS]: arrayOfStringSchema,

    // Cron Expression
    [configRootFields.EXPRESSION]: yup.object().shape({
      [CF.EXPRESSION_MASTER.key]: yup
        .string()
        .trim()
        .required('Master data cron expression is required field'),
      [CF.EXPRESSION_INVENTORY.key]: yup
        .string()
        .trim()
        .required('Inventory cron expression is required field'),
      [CF.EXPRESSION_PRICING.key]: yup
        .string()
        .trim()
        .required('Pricing cron expression is required field'),
      [CF.EXPRESSION_ORDER_SYNC.key]: yup
        .string()
        .trim()
        .required('Order Sync cron expression is required field'),
      [CF.EXPRESSION_RECONCILIATION.key]: yup
        .string()
        .trim()
        .required('Reconciliation cron expression is required field'),
    }),

    // Invoice
    [configRootFields.INVOICE]: yup.object().shape({
      [CF.INVOICE_REG_NO.key]: yup
        .string()
        .trim()
        .required('VAT Registration Number is a required'),
      [CF.INVOICE_PHONE.key]: yup
        .string()
        .trim()
        .required('Phone Number is a required'),
      [CF.INVOICE_ADDRESS.key]: yup
        .string()
        .trim()
        .required('Address is a required'),
    }),
    // SMTP
    [configRootFields.SMTP]: yup.object().shape({
      [CF.SMTP_HOST.key]: yup
        .string()
        .trim()
        .required('SMTP Host is a required field')
        .matches(HOST_REGEX, 'Please provide valid host'),
      [CF.SMTP_PASSWORD.key]: yup
        .string()
        .trim()
        .required('SMTP Password is a required field'),
      [CF.SMTP_FROM_EMAIL.key]: yup
        .string()
        .trim()
        .required('From Email is a required field')
        .matches(EMAIL_REGEX, 'Please Enter a valid email.'),
      [CF.SMTP_FROM_NAME.key]: yup
        .string()
        .trim()
        .required('From Name is a required field'),
      [CF.SMTP_USERNAME.key]: yup
        .string()
        .trim()
        .required('SMTP Username is a required field'),
    }),
    // SQL
    [configRootFields.SQL]: yup.object().shape({
      [CF.SQL_HOST.key]: yup
        .string()
        .trim()
        .required('SQL Host is a required field')
        .matches(HOST_REGEX, {
          message: 'Please provide valid host',
          excludeEmptyString: true,
        }),
      [CF.SQL_PASSWORD.key]: yup
        .string()
        .trim()
        .required('SQL Password is a required field'),
      [CF.SQL_DATABASE_NAME.key]: yup
        .string()
        .trim()
        .required('SQL Database Name is a required field'),
      [CF.SQL_PORT.key]: yup
        .string()
        .trim()
        .required('SQL Port is a required field')
        .matches(NUMBER_REGEX, {
          message: 'SQL Port must be positive numerical value',
          excludeEmptyString: true,
        }),
      [CF.SMTP_USERNAME.key]: yup
        .string()
        .trim()
        .required('SQL Username is a required field'),
    }),

    // MAGENTO
    [configRootFields.MAGENTO]: yup.object().shape({
      [CF.MA_AGGREGATOR.key]: yup.string().trim().optional(),
      [CF.MA_STORE.key]: yup.string().trim().optional(),
      [CF.MA_LIMIT_PRICING.key]: yup
        .string()
        .trim()
        .required('Pricing API Limit is a required field')
        .matches(
          NUMBER_REGEX,
          'Pricing API Limit must be a positive numerical value.',
        ),
      [CF.MA_LIMIT_INVENTORY.key]: yup
        .string()
        .trim()
        .required('Inventory API Limit is a required field')
        .matches(
          NUMBER_REGEX,
          'Inventory API Limit must be a positive numerical value.',
        ),
    }),
    // OTHER
    [CF.ADMIN_EMAIL.key]: yup
      .string()
      .trim()
      .required('Admin email is a required field')
      .matches(MULTIPLE_EMAIL_REGEX, 'Please Enter a valid email.'),
    [CF.FRESH_BARCODE_PREFIX.key]: yup
      .string()
      .trim()
      .optional()
      .test(
        'is-valid-prefix-list',
        'Enter valid comma-separated prefixes',
        (value) => {
          if (!value?.trim()) return true
          const prefixes = value?.split(',').map((p) => p.trim())
          return prefixes.every((p) => /^[a-zA-Z0-9]+$/.test(p))
        },
      ),
    [CF.LOG_DELETION_PERIOD.key]: yup
      .string()
      .trim()
      .required('Log Deletion Period is a required field')
      .matches(
        NUMBER_REGEX,
        'Log Deletion Period must be a positive numerical value.',
      ),
    [CF.MAX_UPLOAD_SIZE.key]: yup
      .string()
      .trim()
      .required('Max Upload Size is a required field')
      .matches(
        NUMBER_REGEX,
        'The maximum upload size must be a positive numerical value.',
      ),
    [CF.MAX_CONCURRENT_AGGREGATORS.key]: yup
      .string()
      .trim()
      .required('Max Concurrent Aggregators is a required field')
      .matches(
        NUMBER_REGEX,
        'The maximum upload size must be a positive numerical value.',
      )
      .test('is-within-range', 'Value must be between 5 and 40', (value) => {
        const num = Number(value)
        return !isNaN(num) && num >= 5 && num <= 40
      })
      .max(40, 'Value must be at most 40'),
  })
  .required()

export const manualCronSchema = yup
  .object()
  .shape({
    lastUpdatedDateTime: yup
      .string()
      .trim()
      .required('Last Updated Date is a required field'),
  })
  .required()
